import React, { useEffect, useRef, useState } from "react";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import DropdownList from "controls/DropdownList";
import { daysOfWeek, parseCurrentHours } from "../Utils";

interface LocationHoursProps {
  showTitle?: boolean;
  editMode?: boolean;
  is24Hours?: boolean;
  setIs24Hours?: (value: boolean) => void;
  currentHours?: any;
  saveHours?: (hours: any) => void;
}

const hours = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const min = i % 2 === 0 ? "00" : "30";
  const period = hour < 12 ? "AM" : "PM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;

  return { id: i, name: `${displayHour}:${min} ${period}` };
});

const LocationHours = (props: LocationHoursProps) => {
    const {showTitle, editMode, is24Hours, setIs24Hours, currentHours, saveHours} = props;
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [businessHours, setBusinessHours] = useState(
    currentHours
      ? parseCurrentHours(currentHours)
      : daysOfWeek.reduce(
          (acc: any, day: string) => ({
            ...acc,
            [day]: { open: "8:00 AM", close: "5:00 PM", closed: false },
          }),
          {}
        )
  );

  const prevRef = useRef(businessHours);

  // Auto-save when hours change
  useEffect(() => {
    if (
      saveHours &&
      parseCurrentHours(prevRef.current) !== parseCurrentHours(businessHours)
    ) {
      saveHours(businessHours);
      prevRef.current = businessHours;
    }
  }, [businessHours, saveHours]);

  // Update local state if parent updates hours
  useEffect(() => {
    if (JSON.stringify(currentHours) !== JSON.stringify(businessHours)) {
      setBusinessHours(parseCurrentHours(currentHours));
    }
  }, [currentHours]);

  const setDayTime = (day: string, type: "open" | "close", newTime: string) => {
    setBusinessHours((prev) => ({
      ...prev,
      [day]: { ...prev[day], [type]: newTime },
    }));
  };

  return (
    <MDBox width="100%" px={1} py={1}>
      {showTitle && (
        <MDTypography
          variant="subtitle2"
          color="text"
          fontWeight="medium"
          mb={1}
        >
          {editMode ? "Select Business Hours" : "Business Hours"}
        </MDTypography>
      )}

      {/* ----------- 24 HOUR SWITCH ----------- */}
      {editMode && (
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={0}
          pb={0}
          borderBottom={!is24Hours ? "1px solid #ddd" : "none"}
        >
          <MDTypography variant="body2" fontWeight="medium">
            Open 24 Hours
          </MDTypography>

          <Switch checked={is24Hours} onChange={(e) => setIs24Hours?.(e.target.checked)} sx={{
    "& .Mui-checked": {
      color: "#21b2a6",           // thumb color when checked
    },
    "& .Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#21b2a6", // track color when checked
    },
  }} />
        </MDBox>
      )}

      {/* ----------- FULL HOURS EDITING MODE ----------- */}
      {editMode && !is24Hours && (
        <>
          {daysOfWeek.map((day) => (
            <MDBox key={day} mb={1.5}>
              {/* DAY + CLOSED SWITCH */}
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="body2" fontWeight="bold">
                  {day}
                </MDTypography>

                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDTypography variant="caption">Closed {day}</MDTypography>

                  <Switch
                    checked={businessHours[day].closed}
                    onChange={(e) => {
                      const closed = e.target.checked;
                      setBusinessHours((prev) => ({
                        ...prev,
                        [day]: closed
                          ? { open: "8:00 AM", close: "5:00 PM", closed: true }
                          : { open: "8:00 AM", close: "5:00 PM", closed: false },
                      }));
                    }}
                  />
                </MDBox>
              </MDBox>

              {/* TIME RANGE (if not closed) */}
              {!businessHours[day].closed && (
                <MDBox display="flex" alignItems="center" gap={2} mt={1}>
                  <DropdownList
                    data={hours}
                    value={businessHours[day].open}
                    setValue={(v) => setDayTime(day, "open", v)}
                    caption=""
                    label="Open"
                    isOpen={openDropdown === `${day}-open`}
                    setOpenDropdown={() =>
                      setOpenDropdown(openDropdown === `${day}-open` ? null : `${day}-open`)
                    }
                  />

                  <MDTypography variant="body2">-</MDTypography>

                  <DropdownList
                    data={hours}
                    value={businessHours[day].close}
                    setValue={(v) => setDayTime(day, "close", v)}
                    caption=""
                    label="Close"
                    isOpen={openDropdown === `${day}-close`}
                    setOpenDropdown={() =>
                      setOpenDropdown(openDropdown === `${day}-close` ? null : `${day}-close`)
                    }
                  />
                </MDBox>
              )}
            </MDBox>
          ))}
        </>
      )}

      {/* ----------- READ-ONLY MODE ----------- */}
      {!editMode && (
        <MDBox>
          {is24Hours ? (
            <MDTypography textAlign="center" variant="body2">
              Open 24 hours
            </MDTypography>
          ) : (
            daysOfWeek.map((day) => (
              <MDBox key={day} mb={0.75}>
                <MDTypography variant="body2" fontWeight="bold">
                  {day}
                </MDTypography>

                {businessHours[day].closed ? (
                  <MDTypography variant="caption" color="text">
                    Closed on {day}
                  </MDTypography>
                ) : (
                  <MDTypography variant="caption" color="text">
                    {businessHours[day].open} – {businessHours[day].close}
                  </MDTypography>
                )}
              </MDBox>
            ))
          )}
        </MDBox>
      )}
    </MDBox>
  );
}
export default LocationHours;